import * as React from "react";
import { useState, useCallback } from "react";
import moment from "moment";
import useMaxIndexRotationLimit from "hooks/useMaxIndexRotationLimit";
import RangeInput from "components/common/RangeInput";
import useTimeSizeOptimizingFixedLeeway from "hooks/useTimeSizeOptimizingFixedLeeway";
export const durationToRoundedDays = (duration) => Math.round(moment.duration(duration).asDays());
const getInitialRangeInDays = (indexLifeTimeMin, indexLifeTimeMax, timeSizeOptimizingFixedLeeway) => timeSizeOptimizingFixedLeeway ? [
  durationToRoundedDays(indexLifeTimeMin),
  durationToRoundedDays(indexLifeTimeMin) + durationToRoundedDays(timeSizeOptimizingFixedLeeway)
] : [durationToRoundedDays(indexLifeTimeMin), durationToRoundedDays(indexLifeTimeMax)];
const YEAR_IN_DAYS = 365;
const getMaxRange = (maxRotationPeriod, maxLifetime, timeSizeOptimizingFixedLeeway) => {
  if (maxRotationPeriod) {
    return timeSizeOptimizingFixedLeeway ? maxRotationPeriod - timeSizeOptimizingFixedLeeway : maxRotationPeriod;
  }
  return maxLifetime >= YEAR_IN_DAYS ? maxLifetime + YEAR_IN_DAYS : YEAR_IN_DAYS;
};
const durationToISOString = (days) => moment.duration(days, "days").toISOString();
const TimeBasedSizeOptimizingStrategyConfiguration = ({
  config: { index_lifetime_max, index_lifetime_min },
  updateConfig,
  disabled = false
}) => {
  const timeSizeOptimizingFixedLeeway = useTimeSizeOptimizingFixedLeeway();
  const [indexLifetimeRange, setIndexLifetimeRange] = useState(
    getInitialRangeInDays(index_lifetime_min, index_lifetime_max, timeSizeOptimizingFixedLeeway)
  );
  const maxRotationPeriod = useMaxIndexRotationLimit();
  const [maxRange, setMaxRange] = useState(
    getMaxRange(
      durationToRoundedDays(maxRotationPeriod),
      indexLifetimeRange[1],
      durationToRoundedDays(timeSizeOptimizingFixedLeeway)
    )
  );
  const isValidRange = useCallback((range) => range[0] < range[1] && range[1] <= maxRange, [maxRange]);
  const validationState = (range) => {
    if (isValidRange(range)) {
      return null;
    }
    return "error";
  };
  const errorMessage = "There needs to be at least 1 day between the minimum and maximum lifetime.";
  const addYearToMaxRange = (currentMax, currentSelectedMax) => {
    if (!maxRotationPeriod && currentMax <= currentSelectedMax) {
      setMaxRange(currentMax + YEAR_IN_DAYS);
    }
  };
  const onRangeChange = (range) => {
    const currentRange = Array.isArray(range) ? range : [range, range + durationToRoundedDays(timeSizeOptimizingFixedLeeway)];
    setIndexLifetimeRange(currentRange);
    addYearToMaxRange(maxRange, currentRange[1]);
    if (isValidRange(currentRange)) {
      updateConfig({
        index_lifetime_min: durationToISOString(currentRange[0]),
        index_lifetime_max: durationToISOString(currentRange[1])
      });
    }
  };
  const maxRotationPeriodHelpText = maxRotationPeriod ? ` The max rotation period is set to ${durationToRoundedDays(maxRotationPeriod)} days by the Administrator.` : "";
  const rangeHelpTitle = timeSizeOptimizingFixedLeeway ? "minimum" : "minimum / maximum";
  const fixedLeewayHint = timeSizeOptimizingFixedLeeway ? ` The maximum number of days is ${durationToISOString(indexLifetimeRange[1])} because the fixed number of days between min and max is set to ${timeSizeOptimizingFixedLeeway}.` : "";
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(
    RangeInput,
    {
      disabled,
      label: "Lifetime in days",
      id: "lifetime-range",
      value: timeSizeOptimizingFixedLeeway ? indexLifetimeRange[0] : indexLifetimeRange,
      help: isValidRange(indexLifetimeRange) ? `The ${rangeHelpTitle} number of days the data in this index is kept before it is retained. ${maxRotationPeriodHelpText} ${fixedLeewayHint}` : errorMessage,
      min: 1,
      step: 1,
      bsStyle: validationState(indexLifetimeRange),
      max: getMaxRange(
        durationToRoundedDays(maxRotationPeriod),
        indexLifetimeRange[1],
        durationToRoundedDays(timeSizeOptimizingFixedLeeway)
      ),
      onAfterChange: (value) => onRangeChange(value)
    }
  ));
};
export default TimeBasedSizeOptimizingStrategyConfiguration;
